<template>
  <RouterView />
</template>

<script lang="ts" setup>
import { useRoute, useRouter } from 'vue-router'
import { onMounted, watch, ref, nextTick } from 'vue'
import Cookies from 'js-cookie'
import useRequest from '@/composables/useRequest'
import { hostBridge } from '@/utils/hostBridge'

const route = useRoute()
const router = useRouter()

// ============================================================
// 用户信息监听（替代 Nuxt useCookie + watch）
// ============================================================
const getUserInfo = () => {
  try {
    const raw = Cookies.get('userInfo')
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

// 监听 userInfo cookie 变化（简单轮询方式，因浏览器不支持 cookie 变化事件）
let prevUserInfo = getUserInfo()
const userInfoWatcher = setInterval(() => {
  const current = getUserInfo()
  if (JSON.stringify(current) !== JSON.stringify(prevUserInfo)) {
    prevUserInfo = current
    if (current && typeof window !== 'undefined') {
      // 退出客服IM登录，清除IM单例缓存
      // imManager.logout()
      sessionStorage.setItem('isFirstLoadIMChat', 'true')
    }
  }
}, 2000)

// ============================================================
// 归因追踪（Attribution）
// ============================================================
const promotionChannelId = ref('')
promotionChannelId.value = (route.query.Channelid ||
  route.query.promotionChannelId ||
  route.query.ChannelId ||
  '') as string

// 解析 URL 参数获取归因信息
const getUrlParams = () => {
  const params: Record<string, string> = {}
  if (typeof window !== 'undefined') {
    const search = window.location.search.slice(1)
    if (search) {
      search.split('&').forEach((item) => {
        const [key, value] = item.split('=')
        if (key && value) params[key] = decodeURIComponent(value)
      })
    }
  }
  return params
}

// 从 Android App 获取归因信息
const getAttributionFromAndroid = () => {
  const params: Record<string, string> = {}
  if (typeof window !== 'undefined' && window.AppInterface) {
    try {
      const channelID = window.AppInterface.getChannelID?.()
      const channelName = window.AppInterface.getChannelName?.()
      const stationID = window.AppInterface.getStationID?.()
      const stationName = window.AppInterface.getStationName?.()
      if (channelID) params.channelID = channelID
      if (channelName) params.channelName = channelName
      if (stationID) params.stationID = stationID
      if (stationName) params.stationName = stationName
      params.source = 'android'
    } catch (e) {
      console.error('[归因] 调用 Android 方法失败：', e)
    }
  }
  return params
}

const saveAttributionToLocalStorage = (params: Record<string, any>) => {
  try {
    if (Object.keys(params).length > 0) {
      const expiryTime = Date.now() + 7 * 24 * 60 * 60 * 1000
      localStorage.setItem(
        'attributionParams',
        JSON.stringify({ ...params, timestamp: Date.now(), expiryTime })
      )
    }
  } catch (e) {
    console.error('保存归因信息失败：', e)
  }
}

const getAttributionFromLocalStorage = () => {
  try {
    const stored = localStorage.getItem('attributionParams')
    if (stored) {
      const attribution = JSON.parse(stored)
      if (attribution.expiryTime && attribution.expiryTime > Date.now())
        return attribution
      else localStorage.removeItem('attributionParams')
    }
  } catch (e) {
    console.error('读取归因信息失败：', e)
  }
  return {}
}

const reportAttribution = async (params: Record<string, any>) => {
  if (!params.channelID && !params.stationID) return
  try {
    const reportedKey = `attribution_reported_${
      params.channelID || params.stationID
    }`
    if (localStorage.getItem(reportedKey)) return
    // await useRequest.post('/java/attribution/report', { ...params, timestamp: Date.now(), url: window.location.href })
    localStorage.setItem(reportedKey, 'true')
  } catch (err) {
    console.error('归因上报失败：', err)
  }
}

const initAttribution = async () => {
  try {
    let params: Record<string, any> = {}
    const urlParams = getUrlParams()
    if (urlParams.channelID || urlParams.stationID) {
      params = {
        channelID: urlParams.channelID || '',
        channelName: urlParams.channelName || '',
        stationID: urlParams.stationID || '',
        stationName: urlParams.stationName || '',
        source: 'url',
      }
    } else {
      const androidParams = getAttributionFromAndroid()
      if (androidParams.channelID || androidParams.stationID) {
        params = androidParams
      } else {
        params = getAttributionFromLocalStorage()
      }
    }
    if (Object.keys(params).length > 0) {
      saveAttributionToLocalStorage(params)
      await reportAttribution(params)
    }
  } catch (error) {
    console.error('归因初始化失败:', error)
  }
}

// 推广渠道访问量上报
const addPromotion = async () => {
  if (promotionChannelId.value) {
    await nextTick()
    await useRequest.get('/java/promotion/v2/addPromotionReg', {
      id: promotionChannelId.value,
    })
  }
}

// ============================================================
// 宿主通信初始化
// ============================================================

/**
 * 从 URL 参数中接收宿主传递的登录信息
 * (已废弃，为安全起见不再通过 URL 传参，改用 postMessage / AppBridge)
 */
const initLoginFromUrl = () => {}

/**
 * 初始化 Host Bridge（H5 iframe 通信）
 * 监听宿主发送的登录成功/登出消息
 */
const initHostBridge = () => {
  const hostType = hostBridge.getHostType()
  console.log('[App] 当前宿主类型:', hostType)

  if (hostType === 'h5') {
    // 监听宿主 H5 发送的登录成功消息
    hostBridge.on('LOGIN_SUCCESS', (payload: any) => {
      if (payload?.token) {
        Cookies.set('token', payload.token, { expires: 7 })
        try {
          sessionStorage.setItem('token', payload.token)
        } catch (e) {}
        if (payload?.uid) {
          Cookies.set('uid', payload.uid, { expires: 7 })
          try {
            sessionStorage.setItem('uid', payload.uid)
          } catch (e) {}
        }
        console.log('[App] 收到宿主登录成功，已写入 Cookie & Session')

        // 转发给子组件的 message 监听器
        // source 用 'app_relay'：不是 'tool_project' （不会被子页面过滤），
        //   也不是外部来源（hostBridge.onMessage 识别并过滤）
        window.dispatchEvent(
          new MessageEvent('message', {
            data: {
              type: 'LOGIN_SUCCESS',
              source: 'app_relay',
              payload: {
                token: payload.token,
                uid: payload.uid || '',
              },
            },
          })
        )
      }
    })

    // 监听宿主 H5 发送的登出消息
    hostBridge.on('LOGOUT', () => {
      Cookies.remove('token')
      Cookies.remove('uid')
      Cookies.remove('userInfo')
      try {
        sessionStorage.removeItem('token')
        sessionStorage.removeItem('uid')
        sessionStorage.removeItem('userInfo')
      } catch (e) {}
      console.log('[App] 收到宿主登出，已清空全局缓存')

      window.dispatchEvent(
        new MessageEvent('message', {
          data: { type: 'LOGOUT', source: 'app_relay' },
        })
      )
    })

    // 监听宿主通过 postMessage 同步的状态栏高度
    hostBridge.on('UPDATE_SAFE_AREA', (payload: any) => {
      if (payload?.height && typeof window !== 'undefined') {
        document.documentElement.style.setProperty(
          '--safe-area-top',
          `${payload.height}px`
        )
        console.log(
          '[App] 收到宿主通过 postMessage 更新的状态栏高度:',
          payload.height,
          'px'
        )
      }
    })

    // 1. 注册完监听器后，立即主动向宿主要求状态栏高度（防止iframe后启动漏掉消息）
    hostBridge.sendToHost('GET_SAFE_AREA')

    // 2. 主动向宿主要求登录态（替代之前的 URL 传参，实现静默同步）
    hostBridge.sendToHost('GET_LOGIN_INFO')
  }
}

if (typeof window !== 'undefined') {
  initLoginFromUrl()
  initHostBridge()
}
/**
 * 在 App 环境下，显式同步 App 原生的登录信息
 *
 * ⭐ 核心变更：不再在 onMounted 中主动调用，
 * 而是等 bridge 就绪事件触发后再获取登录信息，确保 bridge 一定可用。
 */
import { appBridge } from '@/utils/appBridge'
import { setSafeAreaTop } from '@/composables/useSafeArea'
const syncAppLoginState = async () => {
  if (!appBridge.isInApp()) return

  try {
    console.log('[App] 正在同步 App 登录态...')
    const loginInfo = await appBridge.getAppLogin()

    if (!loginInfo.isLogin) {
      // 区分 "bridge 调用失败" 和 "App 确实未登录"
      // _error 存在说明是 bridge 超时/异常，不应清除已有 token
      if (loginInfo._error || loginInfo._fallbackToH5) {
        console.warn('[App] App bridge 调用失败，保留现有 token 不清除')
      } else {
        // App 确实未登录 → 清除 H5 缓存，广播 LOGOUT 通知子页面清空 token ref
        console.log('[App] App 返回未登录，清理 H5 缓存并广播 LOGOUT')
        Cookies.remove('token')
        Cookies.remove('uid')
        try {
          sessionStorage.removeItem('token')
          sessionStorage.removeItem('uid')
        } catch (e) {}
        window.dispatchEvent(
          new MessageEvent('message', {
            data: { type: 'LOGOUT', source: 'app_sync' },
          })
        )
      }
    } else if (loginInfo.token) {
      console.log('[App] App 已登录，同步 token 到 H5')
      Cookies.set('token', loginInfo.token, { expires: 7 })
      Cookies.set('uid', loginInfo.uid || loginInfo.userId || '', {
        expires: 7,
      })
      try {
        sessionStorage.setItem('token', loginInfo.token)
        sessionStorage.setItem('uid', loginInfo.uid || loginInfo.userId || '')
      } catch (e) {}

      console.log('[App] 广播 LOGIN_SUCCESS 通知子页面同步 token ref')
      window.dispatchEvent(
        new MessageEvent('message', {
          data: {
            type: 'LOGIN_SUCCESS',
            source: 'app_sync',
            payload: {
              token: loginInfo.token,
              uid: loginInfo.uid || loginInfo.userId || '',
            },
          },
        })
      )
    }
  } catch (error) {
    console.error('[App] 同步 App 登录态失败:', error)
  }
}

/**
 * 初始化状态栏高度
 */
const initStatusBarHeight = async () => {
  // console.log('进入app,initStatusBarHeight')
  // if (typeof window !== 'undefined') {
  //   const urlParams = new URLSearchParams(window.location.search)
  //   const urlHeight = urlParams.get('statusBarHeight')
  //   console.log('[App] 从URL提取状态栏高度参数:', urlHeight, 'px')
  //   if (urlHeight && urlHeight !== 'null') {
  //     const parsedHeight = parseInt(urlHeight)
  //     if (!isNaN(parsedHeight) && parsedHeight > 0) {
  //       document.documentElement.style.setProperty(
  //         '--safe-area-top',
  //         `${parsedHeight}px`
  //       )
  //       console.log(
  //         '[App] 从URL提取状态栏高度参数并写入CSS变量:',
  //         parsedHeight,
  //         'px'
  //       )
  //       return
  //     }
  //   }
  // }

  const height = await appBridge.getStatusBarHeight()
  setSafeAreaTop(height)  // 更新响应式 ref，NavBar / index.vue 的 navbarHeight computed 自动重渲
  console.log('[App] 状态栏高度已写入 CSS 变量:', height, 'px')
}

onMounted(async () => {
  // 初始化宿主通信
  // initLoginFromUrl()
  // initHostBridge()

  // App 环境：监听 bridge 就绪事件，就绪后再同步登录态
  if (appBridge.isInApp()) {
    let bridgeSynced = false
    const onBridgeReady = async () => {
      if (bridgeSynced) return // 防止多个事件重复触发
      bridgeSynced = true
      console.log('[App] Bridge 就绪，开始同步 App 登录态')
      // 并行执行：登录态同步 和 状态栏高度初始化 互不依赖
      await Promise.all([syncAppLoginState(), initStatusBarHeight()])
    }

    // 1. ⭐ 最早：flutter_inappwebview 插件原生就绪事件（onWebViewCreated 注册完 handler 后触发）
    window.addEventListener(
      'flutterInAppWebViewPlatformReady',
      () => onBridgeReady(),
      { once: true }
    )
    // 2. 兜底：自定义 flutterReady 事件（onLoadStop 注入 bridge 脚本后触发）
    window.addEventListener('flutterReady', () => onBridgeReady(), {
      once: true,
    })
    window.addEventListener('onFlutterReady', () => onBridgeReady(), {
      once: true,
    })

    // 3. 安全网：bridge 可能在监听注册前已就绪（如页面热重载）
    // @ts-ignore
    if (
      window.flutter_inappwebview ||
      window._flutterReady ||
      window.AppInterface
    ) {
      console.log('[App] Bridge 启动时已就绪，直接同步')
      onBridgeReady()
    }

    // 4. 退出登录检测：App 切换回前台时重新校验登录态
    //    场景：用户在原生页面退出登录后返回 H5，此时 Cookie 仍有旧 token
    document.addEventListener('visibilitychange', async () => {
      if (document.visibilityState === 'visible' && bridgeSynced) {
        console.log('[App] 页面重新可见，重新校验 App 登录态')
        await syncAppLoginState()
      }
    })
  } else {
    // 非 App 环境：直接初始化
    await initStatusBarHeight()
  }

  // 归因追踪（与登录态同步并行，互不依赖）
  await Promise.all([initAttribution(), addPromotion()])
  if (promotionChannelId.value) {
    localStorage.setItem('promotionChannelId', promotionChannelId.value)
  }
})

declare global {
  interface Window {
    AppInterface?: {
      getChannelID?: () => string
      getChannelName?: () => string
      getStationID?: () => string
      getStationName?: () => string
      getAppLogin?: () => Promise<any>
      goLogin?: () => void
      goToLogin?: () => void
      goRecharge?: () => void
      showToast?: (message: string) => void
      navigate?: (route: string) => void
      goBack?: () => void
      callFlutter?: (method: string, params: any) => void
      [key: string]: any
    }
    webkit?: {
      messageHandlers?: {
        [key: string]: { postMessage: (message: any) => void }
      }
    }
  }
}
</script>
